import { useEffect, useState, type FormEvent } from 'react';
import { apiGet, apiPost, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { Switch } from '../components/FormCheck';

/** Mirrors backend/src/common/interfaces/specialty.interface.ts's SpecialtyCategory. */
enum SpecialtyCategory {
  PRIMARY_CARE = 'primary_care',
  SURGICAL = 'surgical',
  DIAGNOSTIC = 'diagnostic',
  THERAPEUTIC = 'therapeutic',
  PEDIATRIC = 'pediatric',
  GERIATRIC = 'geriatric',
  MENTAL_HEALTH = 'mental_health',
  EMERGENCY = 'emergency',
  PREVENTIVE = 'preventive',
  OTHER = 'other',
}

const CATEGORY_OPTIONS = Object.values(SpecialtyCategory);

interface Specialty {
  id: string;
  code: string;
  nameEn: string;
  nameEs: string;
  description?: { en: string; es: string };
  category: SpecialtyCategory;
  isActive: boolean;
  /**
   * Whether patients may browse this specialty in the app's Health Center filter.
   * Independent of isActive: a non-patient-facing specialty stays matchable and
   * selectable by specialists, it is just not offered as a browse filter.
   */
  patientFacing?: boolean;
  iconUrl?: string;
  displayOrder: number;
}

/** The two independent booleans this page toggles. */
type FlagField = 'isActive' | 'patientFacing';

const EMPTY_FORM = { code: '', nameEn: '', nameEs: '', category: SpecialtyCategory.OTHER, displayOrder: '' };

/**
 * ADM ops — Specialties (Task D). Admin CRUD over `medical_specialties` — never hard-deletes
 * (no delete button, per backend's UpdateSpecialtyDto — disabling via `isActive` is the only
 * way to remove one from matching/listings, since a specialist may already use that code).
 */
export function SpecialtiesPage() {
  const { data: specialties, loading, error, reload } = useApiResource(
    () => apiGet<Specialty[]>('/admin/specialties'),
    [],
  );

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * Optimistic toggles, keyed `${id}:${field}` so the two independent booleans
   * (isActive, patientFacing) cannot clobber each other mid-flight.
   */
  const [pendingFlags, setPendingFlags] = useState<Record<string, boolean>>({});

  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!specialties || Object.keys(pendingFlags).length === 0) return;
    setPendingFlags((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(prev)) {
        const [id, field] = key.split(':') as [string, FlagField];
        const s = specialties.find((sp) => sp.id === id);
        if (s && (s[field] ?? false) === prev[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [specialties, pendingFlags]);

  /** Optimistic state wins until the reload confirms it (see the effect above). */
  const effectiveFlag = (s: Specialty, field: FlagField) => {
    const key = `${s.id}:${field}`;
    return key in pendingFlags ? pendingFlags[key]! : (s[field] ?? false);
  };

  const toggleFlag = async (specialty: Specialty, field: FlagField, value: boolean) => {
    const key = `${specialty.id}:${field}`;
    setPendingFlags((prev) => ({ ...prev, [key]: value }));
    setBusyId(specialty.id);
    setActionError(null);
    try {
      await apiPatch(`/admin/specialties/${encodeURIComponent(specialty.id)}`, { [field]: value });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Specialty update failed');
      setPendingFlags((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setBusyId(null);
    }
  };

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (!form.code.trim() || !form.nameEn.trim() || !form.nameEs.trim()) {
      setCreateError('Code, English name, and Spanish name are required.');
      return;
    }
    setCreating(true);
    try {
      await apiPost('/admin/specialties', {
        code: form.code.trim(),
        nameEn: form.nameEn.trim(),
        nameEs: form.nameEs.trim(),
        category: form.category,
        ...(form.displayOrder.trim() ? { displayOrder: Number(form.displayOrder) } : {}),
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      reload();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create specialty');
    } finally {
      setCreating(false);
    }
  };

  const showLoadingState = !specialties && loading;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Specialties</h1>
          <p>Manage the medical specialty catalog. Disabling removes a specialty from matching/listings without touching specialists already using it — there is no hard delete.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add specialty'}
        </button>
      </div>

      {showForm && (
        <form className="form-card" style={{ maxWidth: 'none', marginBottom: '1.5rem' }} onSubmit={submitCreate}>
          <h2>New specialty</h2>
          <label htmlFor="code">Code</label>
          <input
            id="code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="cardiology"
            required
          />

          <label htmlFor="nameEn">Name (English)</label>
          <input
            id="nameEn"
            value={form.nameEn}
            onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
            required
          />

          <label htmlFor="nameEs">Name (Spanish)</label>
          <input
            id="nameEs"
            value={form.nameEs}
            onChange={(e) => setForm((f) => ({ ...f, nameEs: e.target.value }))}
            required
          />

          <label htmlFor="category">Category</label>
          <select
            id="category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as SpecialtyCategory }))}
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <label htmlFor="displayOrder">Display order (optional)</label>
          <input
            id="displayOrder"
            type="number"
            value={form.displayOrder}
            onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
          />

          {createError && <p className="auth-error">{createError}</p>}

          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create specialty'}
          </button>
        </form>
      )}

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      <DataTable
        id="specialties"
        rows={specialties}
        loading={showLoadingState}
        empty="No specialties found."
        noun="specialties"
        getRowKey={(s) => s.id}
        columns={[
          { key: 'code', header: 'Code', value: (s) => s.code, width: '110px' },
          { key: 'nameEn', header: 'Name (EN)', value: (s) => s.nameEn },
          { key: 'nameEs', header: 'Name (ES)', value: (s) => s.nameEs },
          { key: 'category', header: 'Category', value: (s) => s.category, filter: 'select' },
          {
            key: 'status',
            header: 'Status',
            value: (s) => (effectiveFlag(s, 'isActive') ? 'Active' : 'Disabled'),
            filter: 'select',
            render: (s) => (
              <StatusBadge
                label={effectiveFlag(s, 'isActive') ? 'Active' : 'Disabled'}
                tone={effectiveFlag(s, 'isActive') ? 'good' : 'neutral'}
              />
            ),
          },
          {
            key: 'enabled',
            header: 'Enabled',
            width: '90px',
            render: (s) => (
              <Switch
                checked={effectiveFlag(s, 'isActive')}
                disabled={busyId === s.id}
                tone="success"
                ariaLabel={`Enable ${s.nameEn}`}
                onChange={(next) => void toggleFlag(s, 'isActive', next)}
              />
            ),
          },
          {
            key: 'patientFacing',
            header: 'Patient-facing',
            width: '120px',
            render: (s) => (
              // Independent of Enabled. Disabling a specialty removes it everywhere;
              // turning this off only takes it out of the app's Health Center browse
              // filter, leaving it matchable. Greyed out when the specialty is
              // disabled, since a disabled specialty is not browsable either way.
              <span
                title={
                  effectiveFlag(s, 'isActive')
                    ? "Show in the app's Health Center filter"
                    : 'Disabled specialties are never shown to patients'
                }
              >
                <Switch
                  checked={effectiveFlag(s, 'patientFacing')}
                  disabled={busyId === s.id || !effectiveFlag(s, 'isActive')}
                  tone="success"
                  ariaLabel={`Show ${s.nameEn} to patients`}
                  onChange={(next) => void toggleFlag(s, 'patientFacing', next)}
                />
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}
